import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { devFilesApi } from '../../services/api';
import { useNavigation } from '../../context/NavigationContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, ReferenceLine } from 'recharts';
import {
  LayoutDashboard,
  ListTodo,
  History,
  Map,
  Database,
  Compass,
  Settings,
  Table2,
  Columns
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip } from '../../components/common/ChartTooltips';

/**
 * DevelopmentDashboardPage - Overview of all dev tools with donut chart tiles
 * TODO: Delete this file before production deployment
 */
const DevelopmentDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    sprint: null,
    revLog: null,
    devPlan: null,
    schema: null,
    navigation: null,
    moduleConfigs: null
  });

  // Count navigation items recursively (the person's tree; a SysAdmin's holds every open row)
  const { tree: navTree } = useNavigation();
  const countNavItems = useCallback((items) => {
    let counts = { total: 0, future: 0, active: 0 };
    items.forEach(item => {
      counts.total++;
      if (item.future) counts.future++;
      else counts.active++;
      if (item.children) {
        const childCounts = countNavItems(item.children);
        counts.total += childCounts.total;
        counts.future += childCounts.future;
        counts.active += childCounts.active;
      }
    });
    return counts;
  }, []);

  // Parse task log JSON for task statuses
  const parseTaskLogJson = useCallback((tasks) => {
    const stats = { pending: 0, inProgress: 0, validate: 0, complete: 0, total: 0 };
    if (!tasks || !Array.isArray(tasks)) return stats;
    tasks.forEach(task => {
      const status = task.status?.toLowerCase();
      if (status === 'pending') stats.pending++;
      else if (status === 'in_progress' || status === 'in progress') stats.inProgress++;
      else if (status === 'validate') stats.validate++;
      else if (status === 'complete') stats.complete++;
      stats.total++;
    });
    return stats;
  }, []);

  // Parse rev log for sawtooth chart - group by major version
  const parseRevLogSawtooth = useCallback((revisions) => {
    if (!revisions || revisions.length === 0) return { data: [], majorBoundaries: [] };

    const stripPrefix = (v) => (v || '').replace(/^[a-zA-Z]+/, '');

    const sorted = [...revisions].sort((a, b) => {
      const vA = parseFloat(stripPrefix(a.version));
      const vB = parseFloat(stripPrefix(b.version));
      return vA - vB;
    });

    // Group by major version
    const majorGroups = {};
    sorted.forEach(rev => {
      const major = parseInt(stripPrefix(rev.version).split('.')[0], 10);
      if (!majorGroups[major]) majorGroups[major] = [];
      majorGroups[major].push(rev);
    });

    const majors = Object.keys(majorGroups).map(Number).sort((a, b) => a - b);
    // Show last ~10 majors
    const recentMajors = majors.slice(-10);

    const data = [];
    const majorBoundaries = [];

    recentMajors.forEach(major => {
      const group = majorGroups[major];
      // Major start point (y=0)
      const majorRev = group.find(r => r.type === 'major') || group[0];
      const majorLabel = `v${major}`;

      majorBoundaries.push({ xIndex: data.length, label: majorLabel });

      data.push({
        x: data.length,
        y: 0,
        version: majorRev.version,
        majorLabel,
        type: 'major'
      });

      // Minor revisions climb up
      const minors = group.filter(r => r.type !== 'major');
      minors.forEach((rev, idx) => {
        data.push({
          x: data.length,
          y: idx + 1,
          version: rev.version,
          majorLabel,
          type: 'minor'
        });
      });
    });

    return { data, majorBoundaries };
  }, []);

  const loadAllStats = useCallback(async () => {
    setLoading(true);
    try {
      const [taskLogRes, revLogRes, devPlanRes, schemaRes, configsRes] = await Promise.all([
        devFilesApi.getTaskLogJson().catch(() => ({ data: { tasks: [] } })),
        devFilesApi.getRevLog().catch(() => ({ data: { revisions: [] } })),
        devFilesApi.getDevPlan().catch(() => ({ data: { steps: [] } })),
        devFilesApi.getLiveSchema().catch(() => ({ data: { tables: [] } })),
        devFilesApi.getModuleConfigs().catch(() => ({ data: { configs: [] } }))
      ]);

      const tasks = taskLogRes.data.tasks || [];
      const taskStats = parseTaskLogJson(tasks);

      const steps = devPlanRes.data.steps || [];
      const planStats = {
        total: steps.length,
        complete: steps.filter(s => s.status === 'Complete').length,
        inProgress: steps.filter(s => s.status === 'In Progress').length,
        notStarted: steps.filter(s => s.status === 'Not Started').length
      };

      const tables = schemaRes.data.tables || [];
      const schemaStats = {
        tables: tables.length,
        columns: tables.reduce((sum, t) => sum + (t.columns?.length || 0), 0)
      };

      const revisions = revLogRes.data.revisions || [];
      const revLogStats = {
        total: revisions.length,
        sawtooth: parseRevLogSawtooth(revisions)
      };

      const navStats = countNavItems(navTree);

      const configs = configsRes.data.configs || [];
      const configNames = configs.map(c => c.tableName?.toLowerCase() || '');
      const tableNames = tables.map(t => t.name?.toLowerCase() || '');
      const configStats = {
        configured: configs.length,
        unconfigured: tableNames.filter(t => !configNames.includes(t)).length,
        total: tables.length
      };

      setStats({
        sprint: taskStats,
        revLog: revLogStats,
        devPlan: planStats,
        schema: schemaStats,
        navigation: navStats,
        moduleConfigs: configStats
      });
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  }, [countNavItems, parseTaskLogJson, parseRevLogSawtooth, navTree]);

  // Recharts donut chart component
  const DonutChart = ({ segments }) => {
    const chartData = segments.filter(s => s.value > 0).map(s => ({
      name: s.label,
      value: s.value,
      fill: s.color,
      colorClass: s.colorClass
    }));
    const total = segments.reduce((sum, s) => sum + s.value, 0);

    if (total === 0) {
      return (
        <div className="stui-dashboard-visual-donut stui-empty stui-md">
          <span className="stui-dashboard-visual-donut-center">0</span>
        </div>
      );
    }

    return (
      <ResponsiveContainer width={100} height={100}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            cx="50%"
            cy="50%"
            innerRadius={30}
            outerRadius={48}
            paddingAngle={1}
            animationDuration={600}
          >
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.fill} stroke="none" />
            ))}
          </Pie>
          <Tooltip content={<PieTooltip />} />
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="stui-dashboard-visual-donut-label-sm">
            {total}
          </text>
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Custom tooltip for sawtooth line chart
  const SawtoothTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="stui-dashboard-visual-tooltip">
        <strong>v{d.version}</strong>
        {d.type === 'major' && <span className="stui-dashboard-visual-tooltip-accent">MAJOR</span>}
      </div>
    );
  };

  // Sawtooth line chart for rev log - grouped by major version
  const SawtoothChart = ({ sawtooth }) => {
    const { data, majorBoundaries } = sawtooth || { data: [], majorBoundaries: [] };

    if (!data || data.length === 0) {
      return <div className="stui-dashboard-visual-chart-empty">No data</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={110}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <XAxis
            dataKey="x"
            tick={({ x, y, payload }) => {
              const boundary = majorBoundaries.find(b => b.xIndex === payload.value);
              if (!boundary) return null;
              return (
                <text x={x} y={y + 10} textAnchor="middle" fontSize={8} fill="#64748b">
                  {boundary.label}
                </text>
              );
            }}
            tickLine={false}
            interval={0}
          />
          <YAxis tick={{ fontSize: 8 }} tickLine={false} axisLine={false} />
          {majorBoundaries.map(b => (
            <ReferenceLine
              key={b.xIndex}
              x={b.xIndex}
              stroke="#f97316"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          ))}
          <Tooltip content={<SawtoothTooltip />} />
          <Line
            type="linear"
            dataKey="y"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3, fill: '#3b82f6' }}
            animationDuration={600}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // Badge display component for schema
  const BadgeDisplay = ({ items }) => (
    <div className="stui-dashboard-visual-badge-group">
      {items.map((item, i) => (
        <div key={i} className={`stui-dashboard-visual-badge ${item.colorClass || ''}`}>
          {item.icon}
          <span className="stui-dashboard-visual-badge-value">{item.value}</span>
          <span className="stui-dashboard-visual-badge-label">{item.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <DashboardLayout
      id="development"
      title="Development Dashboard"
      icon={LayoutDashboard}
      onRefresh={loadAllStats}
      loading={loading}
    >
      {/* Row 1 */}
      <DashboardTile
        title="Task Log"
        icon={ListTodo}
        onClick={() => navigate('/task-log')}
        legend={[
          { colorClass: 'stui-c-yellow',  label: 'Pending',     value: stats.sprint?.pending    || 0 },
          { colorClass: 'stui-c-orange',  label: 'In Progress', value: stats.sprint?.inProgress || 0 },
          { colorClass: 'stui-c-blue',    label: 'Validate',    value: stats.sprint?.validate   || 0 },
          { colorClass: 'stui-c-green',   label: 'Complete',    value: stats.sprint?.complete   || 0 }
        ]}
      >
        <DonutChart
          segments={[
            { value: stats.sprint?.pending    || 0, color: '#fbbf24', colorClass: 'stui-c-yellow', label: 'Pending' },
            { value: stats.sprint?.inProgress || 0, color: '#f97316', colorClass: 'stui-c-orange', label: 'In Progress' },
            { value: stats.sprint?.validate   || 0, color: '#3b82f6', colorClass: 'stui-c-blue',   label: 'Validate' },
            { value: stats.sprint?.complete   || 0, color: '#22c55e', colorClass: 'stui-c-green',  label: 'Complete' }
          ]}
        />
      </DashboardTile>

      <DashboardTile
        title="Dev Plan"
        icon={Map}
        onClick={() => navigate('/dev-plan')}
        legend={[
          { colorClass: 'stui-c-slate',  label: 'Not Started', value: stats.devPlan?.notStarted || 0 },
          { colorClass: 'stui-c-orange', label: 'In Progress', value: stats.devPlan?.inProgress || 0 },
          { colorClass: 'stui-c-green',  label: 'Complete',    value: stats.devPlan?.complete   || 0 }
        ]}
      >
        <DonutChart
          segments={[
            { value: stats.devPlan?.notStarted || 0, color: '#94a3b8', colorClass: 'stui-c-slate',  label: 'Not Started' },
            { value: stats.devPlan?.inProgress || 0, color: '#f97316', colorClass: 'stui-c-orange', label: 'In Progress' },
            { value: stats.devPlan?.complete   || 0, color: '#22c55e', colorClass: 'stui-c-green',  label: 'Complete' }
          ]}
        />
      </DashboardTile>

      <DashboardTile
        title="Rev Log"
        icon={History}
        onClick={() => navigate('/rev-log')}
        colSpan={2}
      >
        <div className="stui-dashboard-visual-sparkline">
          <SawtoothChart sawtooth={stats.revLog?.sawtooth} />
          <div className="stui-dashboard-visual-sparkline-legend">
            <span><span className="stui-dashboard-visual-legend-line stui-blue"></span> Minor</span>
            <span><span className="stui-dashboard-visual-legend-line stui-orange"></span> Major</span>
            <span className="stui-dashboard-visual-sparkline-total">{stats.revLog?.total || 0} total</span>
          </div>
        </div>
      </DashboardTile>

      {/* Row 2 */}
      <DashboardTile
        title="Schema Viewer"
        icon={Database}
        onClick={() => navigate('/schema-viewer')}
      >
        <BadgeDisplay
          items={[
            { icon: <Table2 size={20} />, value: stats.schema?.tables || 0, label: 'Tables', colorClass: 'stui-c-violet' },
            { icon: <Columns size={20} />, value: stats.schema?.columns || 0, label: 'Columns', colorClass: 'stui-c-cyan' }
          ]}
        />
      </DashboardTile>

      <DashboardTile
        title="Navigation"
        icon={Compass}
        onClick={() => navigate('/navigation')}
        legend={[
          { colorClass: 'stui-c-green', label: 'Active', value: stats.navigation?.active || 0 },
          { colorClass: 'stui-c-slate', label: 'Future', value: stats.navigation?.future || 0 }
        ]}
      >
        <DonutChart
          segments={[
            { value: stats.navigation?.active || 0, color: '#22c55e', colorClass: 'stui-c-green', label: 'Active' },
            { value: stats.navigation?.future || 0, color: '#94a3b8', colorClass: 'stui-c-slate', label: 'Future' }
          ]}
        />
      </DashboardTile>

      <DashboardTile
        title="Module Templates"
        icon={Settings}
        onClick={() => navigate('/standard-view-config')}
        legend={[
          { colorClass: 'stui-c-green', label: 'Configured', value: stats.moduleConfigs?.configured   || 0 },
          { colorClass: 'stui-c-slate', label: 'Not Config', value: stats.moduleConfigs?.unconfigured || 0 }
        ]}
      >
        <DonutChart
          segments={[
            { value: stats.moduleConfigs?.configured   || 0, color: '#22c55e', colorClass: 'stui-c-green', label: 'Configured' },
            { value: stats.moduleConfigs?.unconfigured || 0, color: '#94a3b8', colorClass: 'stui-c-slate', label: 'Not Config' }
          ]}
        />
      </DashboardTile>
    </DashboardLayout>
  );
};

export default DevelopmentDashboardPage;
